import { VOICE_PARTS } from '@corocanto/scoring';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { type MyEventDetail, type MyProgramItem, useEventTypes, useMyEvent } from '../api/events';
import { useMembership } from '../auth/session';
import { Person } from '../components/Avatar';
import { GradeChip } from '../components/Chips';
import { ErrorView, Loading } from '../components/Guards';
import { AnswerForm } from '../components/HomeEvents';
import { dateLocale } from '../i18n';
import { daysUntil, formatDayMonth, formatEventDates, formatLength } from '../lib/eventDates';
import ui from '../styles/ui.module.css';
import styles from './MyEventPage.module.css';
import { participationText } from './MyEventsPage';

/**
 * Chorist: one event, read-only like the director's page without its controls. The programme has
 * "Çalış" on every song, so any event's repertoire can be practised, near or far. The chorist can
 * change only their own answer. The line-up shows who sings (names only) to those in it.
 */
export function MyEventPage() {
  const { eventId } = useParams();
  const membership = useMembership();
  const event = useMyEvent(membership?.choirId, eventId);
  if (!membership) return null;
  if (event.isPending) return <Loading />;
  if (event.isError || !event.data) return <ErrorView onRetry={() => void event.refetch()} />;
  return <EventView choirId={membership.choirId} detail={event.data} />;
}

function EventView({ choirId, detail }: { choirId: string; detail: MyEventDetail }) {
  const { t } = useTranslation();
  const locale = dateLocale();
  const types = useEventTypes();
  const event = detail.event;
  const days = daysUntil(event.startsOn);
  const over = daysUntil(event.endsOn ?? event.startsOn) < 0;
  const fields = types.data?.find((x) => x.type === event.type)?.fields ?? [];
  const facts = [
    event.callAndDress,
    ...fields
      .filter((f) => detail.details[f.key] !== undefined)
      .map((f) => {
        const value = detail.details[f.key];
        const shown = f.kind === 'DATE' ? formatDayMonth(String(value), locale) : String(value);
        return `${t(`eventForm.fields.${f.key}`)}: ${shown}`;
      }),
  ].filter(Boolean);
  const songs = detail.program.filter((i) => i.kind === 'SONG');
  const ready = songs.filter((i) => i.grade === 'READY').length;
  const taking = event.participation === 'IN' && event.answer !== 'NOT_ATTENDING';

  return (
    <div className={ui.page}>
      <Link to="/events" className={styles.back}>
        {t('eventPage.back')}
      </Link>
      <div className={ui.pageHeader}>
        <div>
          <div className={styles.titleRow}>
            <h1 data-testid="event-title">{event.name}</h1>
            <span className={`${ui.chip} ${ui.chipNeutral}`}>{t(`eventTypes.${event.type}`)}</span>
            <span className={`${ui.chip} ${ui.chipSoft}`}>{event.limitedLineup ? t('eventPage.lineupLimited') : t('eventPage.lineupWhole')}</span>
          </div>
          <div className={ui.subtitle}>
            {[
              formatEventDates(event.startsOn, event.endsOn, locale),
              event.venue,
              days === 0 ? t('eventPage.today') : days > 0 ? t('eventPage.daysLeft', { count: days }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
          {facts.length > 0 && <div className={ui.subtitle}>{facts.join(' · ')}</div>}
        </div>
        {songs.length > 0 && (
          <Link to={`/events/${event.id}/rehearsal`} className={ui.button}>
            {t('rehearsal.open')}
          </Link>
        )}
      </div>

      {/* Participation and readiness side by side, as the director's attending and ready panes. */}
      <div className={styles.panes}>
        <section className={ui.card} data-testid="my-participation">
          <h2 className={ui.cardTitle}>{t('myEventPage.you')}</h2>
          <p className={styles.noMargin}>
            <strong>{participationText(event, t)}</strong>
            {event.participation === 'EXCLUDED' && event.exclusionReason && (
              <span className={ui.secondary}>{t('myEvents.excludedReason', { reason: event.exclusionReason })}</span>
            )}
            {event.participation === 'IN' && !event.answer && event.replyDeadline && (
              <span className={ui.secondary}>{t('myEvents.awaitingDue', { date: formatDayMonth(event.replyDeadline, locale) })}</span>
            )}
          </p>
          {event.participation === 'IN' && !over && <AnswerForm key={event.answer ?? 'none'} choirId={choirId} event={event} />}
          {event.participation !== 'IN' && songs.length > 0 && <p className={`${ui.muted} ${styles.noMargin}`}>{t('myEventPage.practiceAnyway')}</p>}
        </section>
        {event.readinessTracking && songs.length > 0 && (
          <section className={`${ui.card} ${styles.readyPane}`} data-testid="my-ready">
            <span className={ui.muted}>{t('myEventPage.readiness')}</span>
            <span className={styles.readyNumber}>
              {ready} / {songs.length}
            </span>
            <span className={ui.muted}>{t('myEvents.readyLabel')}</span>
          </section>
        )}
      </div>

      {detail.program.length > 0 && (
        <section className={ui.card}>
          <div className={styles.cardHeader}>
            <h2 className={ui.cardTitle}>{t('eventPage.program')}</h2>
            <span>
              {t('eventPage.total')} <strong>{formatLength(detail.program.reduce((sum, i) => sum + (i.durationSec ?? 0), 0))}</strong>
            </span>
          </div>
          <ol className={styles.program} data-testid="my-program">
            {detail.program.map((item, i) => (
              <ProgramLine
                key={item.id}
                item={item}
                number={detail.program.slice(0, i + 1).filter((x) => x.kind === 'SONG').length}
                askSubmission={taking}
              />
            ))}
          </ol>
        </section>
      )}

      {detail.lineup.length > 0 && (
        <section className={ui.card} data-testid="my-lineup">
          <div className={styles.cardHeader}>
            <h2 className={ui.cardTitle}>{t('myEventPage.lineup')}</h2>
            <span className={ui.muted}>{t('myEventPage.lineupCount', { count: detail.lineup.length })}</span>
          </div>
          <div className={styles.sections}>
            {[...VOICE_PARTS, null].map((part) => {
              const group = detail.lineup.filter((m) => m.part === part);
              if (group.length === 0) return null;
              return (
                <div key={part ?? 'none'}>
                  <div className={styles.sectionName}>{part ? t(`parts.${part}`) : '–'}</div>
                  <ul className={styles.people}>
                    {group.map((m) => (
                      <li key={m.userId} data-testid="lineup-member">
                        <Person name={m.displayName} leadsSection={m.leadsSection} size="small">
                          {m.displayName}
                        </Person>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function ProgramLine({ item, number, askSubmission }: { item: MyProgramItem; number: number; askSubmission: boolean }) {
  const { t } = useTranslation();
  const locale = dateLocale();
  const length = item.durationSec !== null ? formatLength(item.durationSec) : '';
  if (item.kind !== 'SONG') {
    return (
      <li className={styles.programOther}>
        <span />
        <span>{item.kind === 'INTERMISSION' ? t('eventPage.intermission') : `${t('eventPage.speech')}: ${item.text ?? ''}`}</span>
        <span>{length}</span>
        <span />
        <span />
      </li>
    );
  }
  const due = askSubmission && item.submissionRequested && !item.submittedSinceRequest && item.submissionDeadline;
  return (
    <li className={styles.programSong} data-testid="my-program-song">
      <span className={styles.number}>{number}</span>
      <span>
        {item.songTitle}
        {item.songComposer && <span className={ui.secondary}>{item.songComposer}</span>}
      </span>
      <span>{length}</span>
      <span className={styles.standing}>
        <GradeChip grade={item.grade} />
        {item.submissionRequested && item.submissionDeadline && (
          <span className={ui.secondary}>
            {item.submittedSinceRequest ? t('myEventPage.submitted') : t('eventPage.requested', { date: formatDayMonth(item.submissionDeadline, locale) })}
          </span>
        )}
      </span>
      <span className={styles.songActions}>
        <Link to={`/songs/${item.songId}`} className={due ? `${ui.button} ${ui.primary}` : ui.button}>
          {due ? t('myEvents.record') : t('home.practice')}
        </Link>
      </span>
    </li>
  );
}
